/* TypeScript DTOs for Tasks API */

/* Generic wrapper used by some endpoints */
export interface SuccessResponse<T> { data: T; }

/* ---- Core client infrastructure ---- */
export interface ClientConfig {
  baseUrl?: string;
  fetchFn?: typeof fetch;
  defaultHeaders?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(public status: number, public responseBody: string) {
    super(`API Error ${status}: ${responseBody}`);
  }
}

/* ---- DTOs ---- */

// Operational Day Template
export interface OperationalDayTemplateDto {
  id: number;
  name: string;
  description?: string | null;
  templateType?: string | null;
  effectiveFromDate: string; // DateTime ISO
  effectiveToDate?: string | null; // DateTime ISO
  isActive: boolean;
  cronExpression?: string | null; // Quartz cron expression for schedule
}

export interface CreateUpdateOperationalDayTemplateRequest {
  name: string;
  description?: string | null;
  templateType?: string | null;
  cronExpression?: string | null; // Quartz cron expression for schedule
  effectiveFromDate: string; // DateTime ISO
  effectiveToDate?: string | null;
  isActive: boolean;
}

// Task Template
export type StepType = 'text' | 'document' | 'url';
export type EvidenceType = 'photo' | 'signature' | 'text' | 'document' | 'checkbox' | 'numeric' | 'financial' | 'none';

export interface TaskTemplateStepDto {
  id: number;
  order: number;
  stepType: StepType;
  instruction: string;
  linkedDocumentId?: number | null;
  linkedDocumentName?: string | null;
  linkedUrl?: string | null;
  linkedUrlLabel?: string | null;
  evidenceRequired: boolean;
  evidenceType?: EvidenceType | null;
}

export interface TaskStepDto {
  id: number;
  order: number;
  stepType: StepType;
  instruction: string;
  linkedDocumentId?: number | null;
  linkedDocumentName?: string | null;
  linkedUrl?: string | null;
  linkedUrlLabel?: string | null;
  evidenceRequired: boolean;
  evidenceType?: EvidenceType | null;
}

export interface TaskTemplateVersionDto {
  id: number;
  templateId: number;
  versionNumber?: number;
  defaultTaskPriorityId: number;
  expectedDurationMins?: number | null;
  isActive: boolean;
  isGlobalTemplate: boolean;
  steps?: TaskTemplateStepDto[];
  requiresApproval: boolean;
  defaultApproverPersonaId?: number | null;
  defaultApproverUserId?: number | null;
}

export interface TaskTemplateDto {
  id: number;
  name: string;
  description?: string | null;
  taskCategoryId?: number | null;
  versions: TaskTemplateVersionDto[];
}

// Task Category
export interface TaskCategoryDto {
  id: number;
  franchiseId: number;
  name: string;
  description: string;
}

// Task Priority
export interface TaskPriorityDto {
  id: number;
  franchiseId: number;
  name: string;
  description: string;
}

// Task Status
export interface TaskStatusDto {
  id: number;
  franchiseId: number;
  name: string;
  description: string;
}

// Task Schedule
export interface TaskEscalationRuleDto {
  id: number;
  level: number;
  minutesUntilEscalation: number;
  targetRole?: string | null;
  targetUserId?: number | null;
  isEnabled: boolean;
}

export interface TaskScheduleAssignmentDto {
  id: number;
  taskScheduleId: number;
  franchiseId: number;
  franchiseIds?: number[] | null;
  scopeType: 'LOCATION' | 'COLLEAGUE';
  storeNumber?: string | null;
  storeNumbers?: string[] | null;
  personaId?: number | null;
  personaIds?: number[] | null;
  userId?: number | null;
  userIds?: number[] | null;
  assignedAtUtc: string;
  assignedByUserId?: number | null;
  unassignedAtUtc?: string | null;
  escalationRules: TaskEscalationRuleDto[];
  approverPersonaId?: number | null;
  approverUserId?: number | null;
}

export interface TaskScheduleDto {
  id: number;
  taskTemplateId: number;
  templateTask: TaskTemplateDto;
  taskScheduleName: string;
  overrideTaskPriorityId?: number | null;
  cronExpression: string;
  startDate?: string | null;
  endDate?: string | null;
  nextRunDateUtc?: string | null;
  lastGeneratedDateUtc?: string | null;
  assignments?: TaskScheduleAssignmentDto[];
  isActive: boolean;
}

// Dashboard response + nested metrics
export interface TaskStatusMetric { statusId: number; statusName: string; count: number; }
export interface TaskPriorityMetric { priorityId: number; priorityName: string; count: number; }
export interface TaskOverdueMetric { storeOrFranchise: string; overdueCount: number; }
export interface TaskCompletionTrendPoint { day: string; createdCount: number; completedCount: number; }

export interface TaskDashboardResponse {
  statusMetrics: TaskStatusMetric[];
  overdueMetrics: TaskOverdueMetric[];
  completionTrend: TaskCompletionTrendPoint[];
  priorityMetrics: TaskPriorityMetric[];
  totalTasks: number;
  activeTasks: number;
  completedTasks: number;
  overdueTasks: number;
}

// Operational Overview response + nested models
export interface OperationalTimelineItem {
  taskId: number;
  title: string;
  taskTemplateId?: number | null;
  categoryName?: string | null;
  priorityName: string;
  taskPriorityId: number;
  taskStatusId: number;
  statusName: string;
  dueDateTimeUtc?: string | null;
  startDateTimeUtc?: string | null;
  completedDateTimeUtc?: string | null;
  percentageComplete: number;
  isOverdue: boolean;
  assignedUserId?: number | null;
}

export interface OperationalTimelineLane {
  laneType: string;
  items: OperationalTimelineItem[];
}

export interface OperationalOverviewResponse {
  storeNumber: string;
  day: string;
  lanes: OperationalTimelineLane[];
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
}

// Bulk operations
export interface BulkReassignRequest { taskIds: number[]; targetUserId: number; makePrimary: boolean; }
export interface BulkPriorityUpdateRequest { taskIds: number[]; newPriorityId: number; }

// Task type enum for grouping tasks
export type TaskSourceType = 'OperationalDay' | 'Scheduled' | 'System' | 'Workflow' | 'AdHoc';

// Task summary & detail
export interface TaskSummaryDto {
  id: number;
  title: string;
  storeNumber: string;
  storeName?: string;
  targetType?: 'Team' | 'Location';
  categoryName?: string;
  personaName?: string;
  assignedUserId?: number | null;
  assignedUserName?: string;
  taskSourceType?: TaskSourceType;
  statusId: number;
  priorityId: number;
  defaultPriorityId?: number | null;
  startDateUtc?: string | null;
  dueDateUtc?: string | null;
  completedDateUtc?: string | null;
  isOverdue: boolean;
  percentageComplete: number;
}

export interface TaskEvidenceDto {
  id: number;
  evidenceType: string;
  evidenceText?: string | null;
  fileUrl?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  capturedAtUtc: string;
  capturedByUserId: number;
}

export interface TaskEventDto {
  id: number;
  eventType: string;
  oldValue?: string | null;
  newValue?: string | null;
  comment?: string | null;
  createdAtUtc: string;
  createdByUserId?: number | null;
}

export interface TaskDetailDto {
  id: number;
  templateId?: number | null;
  title: string;
  description: string;
  storeNumber: string;
  storeName: string;
  statusId: number;
  priorityId: number;
  defaultPriorityId: number;
  categoryId?: number | null;
  percentageComplete: number;
  dueDateUtc?: string | null;
  startDateUtc?: string | null;
  completedDateUtc?: string | null;
  templateName?: string | null;
  targetUserId?: number | null;
  targetUserName?: string | null;
  targetRoleId?: number | null;
  targetRoleName?: string | null;
  taskSourceType?: TaskSourceType;
  steps: TaskStepDto[];
  evidence: TaskEvidenceDto[];
  history: TaskEventDto[];
  requiresApproval: boolean;
  approverUserId?: number | null;
  approverUserName?: string | null;
  approverPersonaId?: number | null;
  approverPersonaName?: string | null;
  approvalResult?: 'Compliant' | 'NonCompliant' | null;
  approvalNotes?: string | null;
  approvalDateUtc?: string | null;
  approvalByUserId?: number | null;
}

// Request DTOs
export interface ExtendDueTimeRequest {
  newDueDateTimeUtc: string;
  reason: string;
}

export interface SubmitForApprovalRequest {
  notes?: string | null;
}

export interface ApprovalDecisionRequest {
  decision: 'Compliant' | 'NonCompliant';
  notes?: string | null;
  sendBackForRework: boolean;
}

export interface CreateTaskRequest {
  title: string;
  description?: string | null;
  storeNumber: string;
  storeName?: string | null;
  taskTemplateId?: number | null;
  taskPriorityId: number;
  dueDateTimeUtc?: string | null;
  taskType?: string | null;
  resourceId?: number | null;
  targetUserId?: number | null;
  targetUserName?: string | null;
  targetRoleId?: number | null;
  targetRoleName?: string | null;
}

export interface ReassignTaskRequest { newUserId: number; }
export interface BulkReassignTasksRequest { taskIds: number[]; newUserId: number; }
export interface ChangePriorityRequest { newPriorityId: number; }
export interface BulkChangePriorityRequest { taskIds: number[]; newPriorityId: number; }
